/**
 * Parses URL query string parameters into a key-value object.
 *
 * Handles URL decoding and converts '+' characters to spaces.
 * Supports query strings with or without leading '?' or '#'.
 *
 * @param query - The query string to parse (with or without leading ? or #)
 * @returns Object containing the parsed query parameters
 *
 * @example
 * ```typescript
 * getUrlParams('?name=John&age=30') // returns { name: 'John', age: '30' }
 * getUrlParams('search=hello+world') // returns { search: 'hello world' }
 * getUrlParams('') // returns {}
 * ```
 */
export const getUrlParams = (
  query: string,
): {
  [key: string]: string
} => {
  if (!query) {
    return {}
  }

  return (/^[?#]/.test(query) ? query.slice(1) : query)
    .split('&')
    .reduce<Record<string, any>>((params, param) => {
      const [key, value] = param.split('=')
      if (!key) return params
      return {
        ...params,
        [key]: value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '',
      }
    }, {})
}

/**
 * Extracts the hostname from a URL string.
 *
 * @param url - The URL string to extract hostname from
 * @returns The hostname if found, null if the URL is invalid or doesn't match the pattern
 *
 * @example
 * ```typescript
 * getUrlHostname('https://example.com/path') // returns 'example.com'
 * getUrlHostname('http://subdomain.example.com') // returns 'subdomain.example.com'
 * getUrlHostname('invalid-url') // returns null
 * ```
 */
export const getUrlHostname = (url: string): string | null | undefined => {
  const matches = url.match(/https?:\/\/([^/]+)/)
  if (matches && matches.length > 0) return matches[1]
  return null
}

/**
 * Splits a URL into its path segments, filtering out empty strings.
 *
 * @param url - The URL string to split into parts
 * @returns Array of non-empty URL path segments
 *
 * @example
 * ```typescript
 * getUrlParts('https://example.com/path/to/resource') // returns ['https:', 'example.com', 'path', 'to', 'resource']
 * getUrlParts('/api/users/123') // returns ['api', 'users', '123']
 * ```
 */
export const getUrlParts = (url: string): string[] => {
  return url.split('/').filter(Boolean)
}
